import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Simple in-memory cache
const cache = {
  perfumes: { data: null, timestamp: 0 },
  CACHE_TTL: 60000, // 1 minute cache
};

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to set Content-Type based on data type
api.interceptors.request.use((config) => {
  // Don't set Content-Type for FormData - let browser handle it with proper boundary
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getMe: () => api.get('/auth/me'),
};

// Perfume APIs with caching
export const perfumeAPI = {
  getAll: async () => {
    const now = Date.now();
    if (cache.perfumes.data && (now - cache.perfumes.timestamp) < cache.CACHE_TTL) {
      return { data: cache.perfumes.data };
    }
    const response = await api.get('/perfumes');
    cache.perfumes.data = response.data;
    cache.perfumes.timestamp = now;
    return response;
  },
  invalidateCache: () => {
    cache.perfumes.data = null;
    cache.perfumes.timestamp = 0;
  },
  getOne: (id) => api.get(`/perfumes/${id}`),
  create: (data) => { perfumeAPI.invalidateCache(); return api.post('/perfumes', data); },
  update: (id, data) => { perfumeAPI.invalidateCache(); return api.put(`/perfumes/${id}`, data); },
  delete: (id) => { perfumeAPI.invalidateCache(); return api.delete(`/perfumes/${id}`); },
  addStock: (id, quantity_liters) => { perfumeAPI.invalidateCache(); return api.post(`/perfumes/${id}/add-stock`, { quantity_liters }); },
  getLowStock: () => api.get('/perfumes/low-stock'),
};

// Sales APIs
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  create: (data) => api.post('/sales', data),
  delete: (id) => api.delete(`/sales/${id}`),
};

// Revenue APIs
export const revenueAPI = {
  getAll: (params) => api.get('/revenue', { params }),
  create: (data) => api.post('/revenue', data),
  getSummary: (period) => api.get('/revenue/summary', { params: { period } }),
};

// Costs APIs
export const costsAPI = {
  get: () => api.get('/costs'),
  update: (data) => api.put('/costs', data),
};

// Scorporo API
export const scorporoAPI = {
  calculate: (data) => api.post('/scorporo', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getOps: () => api.get('/dashboard/ops'),
};

// Reports API
export const reportsAPI = {
  getSalesReport: (start_date, end_date) => 
    api.get('/reports/sales', { params: { start_date, end_date } }),
};

// Transit Orders API
export const transitOrdersAPI = {
  getAll: (order_type) => api.get('/transit-orders', { params: order_type ? { order_type } : {} }),
  create: (data) => api.post('/transit-orders', data),
  delete: (id) => api.delete(`/transit-orders/${id}`),
};

// Orders API (Multi-item orders with cart)
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getAllIncludeLive: () => api.get('/orders', { params: { include_live: true } }),
  list: async () => {
    // Get all orders for filtering (used by PendingOrders page)
    const res = await api.get('/orders', { params: { limit: 5000 } });
    return res.data?.orders || [];
  },
  listFolders: async () => {
    const res = await api.get('/orders', { params: { in_folder: true, limit: 5000 } });
    return res.data?.orders || [];
  },
  getOne: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  createLive: (data) => api.post('/orders', { ...data, is_live: true }),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  getPrices: () => api.get('/prices'),
  uploadImage: (id, formData) => api.post(`/orders/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Fillings API (Registro Riempimento)
export const fillingsAPI = {
  getAll: (params) => api.get('/fillings', { params }),
  create: (data) => api.post('/fillings', data),
  delete: (id) => api.delete(`/fillings/${id}`),
};

// Prices API
export const pricesAPI = {
  getAll: () => api.get('/prices'),
  getSpecial: () => api.get('/prices/special'),
  getPrice: (perfumeCode, format, channel) => api.get(`/prices/${perfumeCode}/${format}/${channel}`),
};

// Live Sync API (for cross-device sync)
export const liveAPI = {
  getState: () => api.get('/live/state'),
  addOrder: (order) => api.post('/live/orders', order),
  removeOrder: (orderId) => api.delete(`/live/orders/${orderId}`),
  setShowcase: (perfume) => api.post('/live/showcase', perfume),
  clearShowcase: () => api.delete('/live/showcase'),
  setText: (text, duration) => api.post('/live/text', { text, duration }),
  clearText: () => api.delete('/live/text'),
  setFakeRevenue: (amount) => api.post('/live/fake-revenue', { amount }),
  addFakeRevenue: (amount) => api.post('/live/fake-revenue/add', { amount }),
  resetFakeRevenue: () => api.delete('/live/fake-revenue'),
};

// WooCommerce API
export const woocommerceAPI = {
  test: () => api.get('/woocommerce/test'),
  getOrders: (params) => api.get('/woocommerce/orders', { params }),
  importOrders: (after) => api.post('/woocommerce/import', null, { params: after ? { after } : {} }),
  importMarch: () => api.post('/woocommerce/import-march'),
  sync: () => api.get('/woocommerce/sync'),  // Lightweight sync for auto-refresh
};

// TikTok API
export const tiktokAPI = {
  importPicking: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/tiktok/import-picking', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export default api;
